<template>
    <div class="flow-params_wrapper">
        <or-select
            v-if="entitySelectable"
            class="parameter-type--selector"
            :label="$t('common.intent.param_input.parameterTypeSelector.label')"
            :options="parameterTypeOptions"
            :keys="{ label: 'label', value: 'value' }"
            v-model="parameterType"
        />
        <div
          v-if="parameterType === 'custom'"
          class="flow-params_input"
        >
            <or-icon-button
                class="flat code"
                icon="code"
                type="secondary"

                disableRipple

                :class="{isActive: codeMode}"
                :disabled="disabled"

                @click="changeMode"
            />

            <or-code
                v-if="codeMode"

                v-model="localValue"

                :error="$t('common.intent.param_input.parameterTypeSelector.errors.invalidJson')"
                mode="json"

                disableVariables

                :label="label"
                :invalid="!isValidJSON"
                :fullScreen="false"
            />

            <or-textbox
                v-else
                class="flow-input"
                v-model="localText"

                multiLine
                :rows="1"

                :label="label"
                :disabled="disabled"
            />
        </div>
        <div
          v-else
          class="flow-params_input entity-input"
        >
            <or-select
                class="entity-selection"
                :label="label"
                :options="entitiesList"
                :value="flowEntityParameter"
                @select="onEntitySelection"
            >
                <template slot="option" slot-scope="item">
                  <div class="entity-option">
                      <!-- if first item -->
                      <div
                        v-if="!item.option.role"
                        class="entity-option--label"
                      >
                          {{getEntityOptionLabel(item.option)}}
                      </div>
                      <div
                          v-else
                          class="entity-option--no-label"
                      >&nbsp;</div>
                      <span class="entity-option--name">
                        {{item.option.label}}
                      </span>
                  </div>
                </template>
            </or-select>
            <or-icon-button
                class="open-entities--btn"
                @click="openEntities"
                :tooltip="$t('common.intent.param_input.entitySelector.openEntities.tooltip')"
                tooltip-position="top center"
            >
                {}
            </or-icon-button>
        </div>
    </div>
</template>

<script>
import KnowledgeBus from '@/KnowledgeBus'
import { mapState } from 'vuex'
import { getEntityTypeLabel } from '@/components/model/nlu-model/sidebar/entities/entity-types'

export default {
  mounted () {
    if (this.value && !this.isString) {
      this.changeMode()
    }
  },

  props: {
    entitySelectable: Boolean,
    label: String,
    value: {
      type: Object,
      default: () => ({
        type: 'custom',
        value: '""'
      })
    },
    disabled: Boolean
  },

  computed: {
    ...mapState('knowledgebase/entities', ['allEntities']),

    entitiesList () {
      return this.allEntities.value.reduce((acc, entity) => {
        acc.push({
          entityId: entity.id,
          name: entity.name,
          label: entity.name,
          value: entity.name,
          type: entity.type,
          role: undefined
        })
        entity.roles.forEach(role => {
          acc.push({
            entityId: entity.id,
            name: entity.name,
            label: `${entity.name}:${role}`,
            value: `${entity.name}:${role}`,
            type: entity.type,
            role
          })
        })
        return acc
      }, [])
    },

    isString () {
      try {
        return _.isString(JSON.parse(this.localValue))
      } catch (err) {
        return false
      }
    },

    isValidJSON () {
      try {
        JSON.parse(this.localValue)
        return true
      } catch (err) {
        return false
      }
    },

    parameterTypeOptions () {
      return [
        {
          label: this.$t('common.intent.param_input.parameterTypeSelector.customParam'),
          value: 'custom'
        }, {
          label: this.$t('common.intent.param_input.parameterTypeSelector.entity'),
          value: 'entity'
        }
      ]
    },

    parameterType: {
      get () {
        return this.value.type || 'custom'
      },
      set (parameterType) {
        if (parameterType === this.parameterType) {
          return
        }
        this.codeMode = false
        this.$emit('input', {
          value: parameterType === 'custom' ? '""' : '',
          type: parameterType
        })
      }
    },

    localValue: {
      get () {
        return this.value.value || ''
      },

      set (value) {
        this.$emit('input', {
          ...this.value,
          value
        })
      }
    },

    localText: {
      get () {
        if (this.value.value && _.includes([this.value.value[0], this.value.value[this.value.value.length - 1]], '"')) {
          return this.value.value.slice(1, -1)
        }

        return this.value.value || ''
      },

      set (value) {
        this.$emit('input', {
          ...this.value,
          value: `"${value}"`
        })
      }
    },

    flowEntityParameter () {
      try {
        const parsedEntity = JSON.parse(this.value.value)
        let entityValue = parsedEntity.name
        if (parsedEntity.role) {
          entityValue += `:${parsedEntity.role}`
        }
        return entityValue || ''
      } catch (error) {
        return ''
      }
    }
  },

  data () {
    return {
      codeMode: false
    }
  },

  methods: {
    getEntityOptionLabel (option) {
      return getEntityTypeLabel(option.type)
    },

    onEntitySelection (selectedEntity) {
      this.$emit('input', {
        ...this.value,
        value: JSON.stringify(
          selectedEntity
            ? {
                entityId: selectedEntity.entityId,
                name: selectedEntity.name,
                type: selectedEntity.type,
                role: selectedEntity.role || undefined
              }
            : {}
        )
      })
    },
    changeMode () {
      this.codeMode = !this.codeMode
    },

    openEntities () {
      KnowledgeBus.$emit('sidebar::open', 4)
    }
  }
}
</script>

<style lang="scss" scoped>
.flow-params_wrapper{
    display: flex;
    .parameter-type--selector {
        margin-bottom: 0;
    }
    .flow-params_input {
        flex: 1 1 auto;
        margin-left: 16px;
        position: relative;

        .ui-textbox {
            margin-bottom: 0;
        }

        & > .code {
            position: absolute;
            right: 0;
            top: 0;
            width: 32px;
            height: 32px;
        }
        ::v-deep .or-code {
            .header {
                margin-top: 3px;
                margin-bottom: 2px;
            }
        }

        &.entity-input {
            display: flex;
            .entity-selection {
                flex-grow: 2;
            }
            .open-entities--btn {
                margin-top: 36px;
                margin-right: -6px;
                margin-left: 4px;
                background: $or-white;
                ::v-deep .ui-icon-button__icon {
                    color: #6c747d;
                }
            }
        }
    }

    .entity-option {
        display: flex;
        .entity-option--label, .entity-option--no-label {
            width: 20px;
            max-width: 20px;
            margin: 0 8px 0 4px;
        }
        .entity-option--label {
            color: $or-grey;
        }
    }

    @media (max-width: 1024px)  {
      flex-direction: column;
      .parameter-type--selector {
        width: 100%;
      }
      .flow-params_input {
        width: 100%;
        margin-left: 0;
      }
    }
}
</style>

<style lang="scss">
.parameter-type--selector {
    min-width: 140px;
    width: 20%;
}
.flow-params_input .isActive > .ui-icon-button__icon{
    color: $or-blue !important;
}
</style>
